import { createContext, useContext, useEffect, useState } from "react";

const CartCtx = createContext(null);

export function parsePrice(s) {
  if (!s) return 0;
  const m = String(s).match(/\$?\s*([0-9]+(?:\.[0-9]{1,2})?)/);
  return m ? parseFloat(m[1]) : 0;
}

// In-stock variants only
function inStockVariants(p) {
  return (p?.variants || []).filter((v) => v && v.in_stock !== false);
}

// A short price label for cards/listings.
export function priceLabel(p) {
  const vs = (p?.variants || []).filter((v) => v && Number(v.price) > 0);
  if (vs.length) {
    const prices = vs.map((v) => Number(v.price) || 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? `$${min.toFixed(2)}` : `From $${min.toFixed(2)}`;
  }
  return p?.price_display || "Contact for pricing";
}

export function hasVariants(p) {
  return (p?.variants || []).length > 0;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("hat_cart") || "[]");
      // migrate older carts that predate variant keys
      return raw.map((x) => ({ ...x, key: x.key || `${x.product_id}::${x.variant_id || ""}` }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("hat_cart", JSON.stringify(items));
  }, [items]);

  // variant is optional: { id, label, price }
  const addItem = (p, qty = 1, variant = null) => {
    const variantId = variant?.id || "";
    const key = `${p.id}::${variantId}`;
    const unitPrice = variant
      ? Number(variant.price) || 0
      : (p.price || parsePrice(p.price_display));
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [
        ...prev,
        {
          key,
          product_id: p.id,
          slug: p.slug,
          name: p.name,
          variant_id: variantId,
          variant_label: variant?.label || "",
          price_display: variant ? `$${unitPrice.toFixed(2)}` : (p.price_display || ""),
          unit_price: unitPrice,
          image_url: p.image_url || "",
          qty,
        },
      ];
    });
  };

  const removeItem = (key) => setItems((prev) => prev.filter((x) => x.key !== key));
  const updateQty = (key, qty) =>
    setItems((prev) => prev.map((x) => (x.key === key ? { ...x, qty: Math.max(1, qty) } : x)));
  const clear = () => setItems([]);

  const count = items.reduce((s, x) => s + x.qty, 0);
  const subtotal = items.reduce((s, x) => s + x.unit_price * x.qty, 0);

  return (
    <CartCtx.Provider value={{ items, addItem, removeItem, updateQty, clear, count, subtotal, inStockVariants }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
